import { useEffect, useState } from "react";

import styled from "styled-components";
import { message, Modal, Input, Popconfirm } from "antd";

import { deleteComment, postComment, putComment } from "../../api/slideAPI";
import { checkUserName } from "../../api/authAPI";
import { hasTempViewerSession } from "../../api/api";
import {
  ensureTempViewerIdentity,
  ensureTempViewerPassword,
  getStoredTempViewerName,
} from "../../lib/tempViewer";

interface Comment {
  slideCommentId: number;
  content: string;
  writer: string;
  anonymous?: boolean;
}

const ADMIN_ROLES = ["ADMIN_MAIN", "ADMIN_SUB"];

interface Props {
  slideId: number;
  comments: Array<Comment>;
  reloadComments: () => void;
  isSharedView?: boolean;
  userRole?: string;
}

type PendingAction = { type: "edit"; commentId: number } | { type: "delete"; commentId: number };

const CommentArea = ({ slideId, comments, reloadComments, isSharedView = false, userRole = "" }: Props) => {
  const isAdmin = ADMIN_ROLES.includes(userRole);
  const [newComment, setNewComment] = useState("");
  const [userName, setUserName] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const isTempViewer = hasTempViewerSession();

  const handleCommentSubmit = async (anonymousPassword?: string) => {
    if (!newComment.trim()) return;

    const tempViewerIdentity = isTempViewer ? ensureTempViewerIdentity() : null;
    if (isTempViewer && !tempViewerIdentity) return;

    const anonPayload = anonymousPassword
      ? { anonymousPassword }
      : tempViewerIdentity
        ? { anonymousPassword: tempViewerIdentity.anonymousPassword }
        : undefined;

    const result = editingCommentId
      ? await putComment(editingCommentId, newComment.trim(), anonPayload)
      : await postComment(slideId, newComment.trim(), tempViewerIdentity ?? undefined);

    if (!result) {
      message.error(editingCommentId ? "댓글 수정에 실패했습니다." : "댓글 등록에 실패했습니다.");
      return;
    }

    message.success(editingCommentId ? "댓글이 수정되었습니다." : "댓글이 등록되었습니다.");
    setNewComment("");
    setEditingCommentId(null);
    reloadComments();
  };

  const startEdit = (comment: Comment) => {
    if (isSharedView && comment.anonymous) {
      setPendingAction({ type: "edit", commentId: comment.slideCommentId });
      setPasswordInput("");
      setPasswordModalOpen(true);
      return;
    }
    setEditingCommentId(comment.slideCommentId);
    setNewComment(comment.content);
  };

  const handleDeleteComment = async (commentId: number) => {
    if (isSharedView) {
      setPendingAction({ type: "delete", commentId });
      setPasswordInput("");
      setPasswordModalOpen(true);
      return;
    }

    const anonymousPassword = isTempViewer ? ensureTempViewerPassword() : null;
    if (isTempViewer && !anonymousPassword) return;

    const result = await deleteComment(commentId, anonymousPassword ?? undefined);
    if (!result) {
      message.error("댓글 삭제에 실패했습니다.");
      return;
    }

    message.success("댓글이 삭제되었습니다.");
    reloadComments();
  };

  const handlePasswordConfirm = async () => {
    if (!pendingAction) return;
    if (!passwordInput.trim()) {
      message.warning("비밀번호를 입력해주세요.");
      return;
    }

    if (pendingAction.type === "delete") {
      const result = await deleteComment(pendingAction.commentId, passwordInput);
      if (!result) {
        message.error("비밀번호가 올바르지 않거나 삭제에 실패했습니다.");
        return;
      }
      message.success("댓글이 삭제되었습니다.");
      reloadComments();
    } else if (pendingAction.type === "edit") {
      const comment = comments.find((c) => c.slideCommentId === pendingAction.commentId);
      if (!comment) return;

      // 기존 내용 그대로 putComment 호출해 비밀번호 서버 검증
      const result = await putComment(pendingAction.commentId, comment.content, { anonymousPassword: passwordInput });
      if (!result) {
        message.error("비밀번호가 올바르지 않습니다.");
        return;
      }

      setPasswordModalOpen(false);
      setPendingAction(null);
      setEditingCommentId(pendingAction.commentId);
      setNewComment(comment.content);
      setPendingEditPassword(passwordInput);
      setPasswordInput("");
      return;
    }

    setPasswordModalOpen(false);
    setPendingAction(null);
    setPasswordInput("");
  };

  const [pendingEditPassword, setPendingEditPassword] = useState<string>("");

  const handleSubmitWithEditPassword = async () => {
    if (editingCommentId && pendingEditPassword) {
      await handleCommentSubmit(pendingEditPassword);
      setPendingEditPassword("");
    } else {
      await handleCommentSubmit();
    }
  };

  useEffect(() => {
    if (isTempViewer) {
      setUserName(getStoredTempViewerName());
      return;
    }

    checkUserName().then((name) => setUserName(name));
  }, [comments, isTempViewer]);

  return (
    <>
    <StCommentAreaContainer>
      <h3 style={{ margin: 0 }}>Comments</h3>
      {comments.map((comment) => (
        <StCommentItem key={comment.slideCommentId}>
          <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "4px" }}>
            {comment.writer}
            {comment.anonymous && (
              <StTempUserLabel>(링크 접근 사용자)</StTempUserLabel>
            )}
            {isSharedView
              ? userName === comment.writer && comment.anonymous && (
                  <>
                    <StActionBtn type="button" onClick={() => startEdit(comment)}>Edit</StActionBtn>
                    <StDeleteBtn onClick={() => handleDeleteComment(comment.slideCommentId)}>Delete</StDeleteBtn>
                  </>
                )
              : (comment.anonymous ? isAdmin : userName === comment.writer) && (
                  <>
                    {!comment.anonymous && (
                      <StActionBtn type="button" onClick={() => startEdit(comment)}>Edit</StActionBtn>
                    )}
                    <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDeleteComment(comment.slideCommentId)} okText="삭제" cancelText="취소">
                      <StDeleteBtn>Delete</StDeleteBtn>
                    </Popconfirm>
                  </>
                )
            }
          </div>
          <div style={{ fontSize: "18px" }}>{comment.content}</div>
        </StCommentItem>
      ))}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", marginTop: "auto" }}>
        <input
          type="text"
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          style={{
            flex: 1,
            padding: "8px 12px",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            fontSize: "14px"
          }}
        />
        <button
          onClick={handleSubmitWithEditPassword}
          style={{
            padding: "8px 16px",
            backgroundColor: "#1890ff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {editingCommentId ? "Update" : "Send"}
        </button>
        {editingCommentId && (
          <button
            onClick={() => {
              setEditingCommentId(null);
              setNewComment("");
              setPendingEditPassword("");
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#f5f5f5",
              color: "#333",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </StCommentAreaContainer>
    <Modal
      title="비밀번호 확인"
      open={passwordModalOpen}
      onOk={handlePasswordConfirm}
      onCancel={() => { setPasswordModalOpen(false); setPendingAction(null); setPasswordInput(""); }}
      okText="확인"
      cancelText="취소"
      centered
    >
      <Input.Password
        placeholder="등록 시 사용한 비밀번호를 입력하세요"
        value={passwordInput}
        onChange={(e) => setPasswordInput(e.target.value)}
        onPressEnter={handlePasswordConfirm}
      />
    </Modal>
    </>
  );
};

const StCommentAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  overflow-y: auto;
`;

const StCommentItem = styled.div`
  padding: 8px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
`;

const StDeleteBtn = styled.button`
  float: right;
  padding: 4px 8px;
  background-color: #ff4d4f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
`;

const StActionBtn = styled.button`
  float: right;
  padding: 4px 8px;
  margin-left: 6px;
  background-color: #1677ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
`;

const StTempUserLabel = styled.span`
  margin-left: 0.375rem;
  font-size: 0.6875rem;
  color: #aaa;
  font-weight: normal
`

export default CommentArea;
